import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit3,
  Loader2,
  Power,
  MapPin,
  Save,
  X,
  Package,
  ImagePlus,
} from "lucide-react";
import { toast } from "sonner";

import { deliveryApi } from "../services/deliveryApi";
import { resolveBackendImage } from "../utils/brandLogo";
import DeliveryNav from "../components/delivery/DeliveryNav";
import ProdutoFormAbaInfo from "../components/delivery/ProdutoFormAbaInfo";
import ProdutoFormAbaComplementos from "../components/delivery/ProdutoFormAbaComplementos";
import ProdutoFormAbaDisponibilidade from "../components/delivery/ProdutoFormAbaDisponibilidade";
import ProdutoFormAbaEstoque from "../components/delivery/ProdutoFormAbaEstoque";

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function DeliveryProdutos() {
  const { negocioId } = useParams();
  const [loja, setLoja] = useState(null);
  const [cardapio, setCardapio] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("produtos");
  const [novaCategoriaNome, setNovaCategoriaNome] = useState("");

  // form states
  const [editingProduto, setEditingProduto] = useState(null);
  const [editingZona, setEditingZona] = useState(null);

  const reload = useCallback(async () => {
    try {
      const l = await deliveryApi.getLojaByNegocio(negocioId);
      setLoja(l);
      const card = await deliveryApi.getCardapio(l.id);
      setCardapio(card.cardapio || null);
      setCategorias((card.cardapio?.categorias) || []);
      setProdutos(card.produtos || []);
      setZonas(card.zonas || []);
    } catch (err) {
      toast.error(
        err?.response?.status === 404
          ? "Ative o Delivery antes de cadastrar produtos"
          : "Erro ao carregar dados"
      );
    } finally {
      setLoading(false);
    }
  }, [negocioId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ===== Produtos handlers =====
  const _sanitizeProdutoPayload = (data) => {
    // Remove IDs temporários (`_tmp_*`) — backend gera os definitivos
    const grupos = (data.grupos_complementos || []).map((g) => ({
      ...g,
      id: g.id && !String(g.id).startsWith("_tmp_") ? g.id : undefined,
      opcoes: (g.opcoes || []).map((o) => ({
        ...o,
        id: o.id && !String(o.id).startsWith("_tmp_") ? o.id : undefined,
      })),
    }));
    const payload = {
      nome: data.nome,
      descricao: data.descricao,
      preco: parseFloat(data.preco),
      categoria_id: data.categoria_id || null,
      disponivel: data.disponivel,
      destaque: !!data.destaque,
      // Fase C
      unidade_medida: data.unidade_medida || "unidade",
      tipo_produto: data.tipo_produto || "artesanal",
      restricoes_alimentares: data.restricoes_alimentares || [],
      disponibilidade_horario: data.disponibilidade_horario || null,
      grupos_complementos: grupos,
      // Iter3
      tipo_disponibilidade: data.tipo_disponibilidade || "pronta_entrega",
      encomenda_config: data.encomenda_config || null,
      estoque: data.estoque || null,
    };
    return payload;
  };

  const handleSaveProduto = async (data) => {
    try {
      const payload = _sanitizeProdutoPayload(data);
      if (editingProduto?.id) {
        await deliveryApi.updateProduto(editingProduto.id, payload);
        toast.success("Produto atualizado");
      } else {
        await deliveryApi.createProduto({ loja_id: loja.id, ...payload });
        toast.success("Produto criado");
      }
      setEditingProduto(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar produto");
    }
  };

  const handleToggleDisp = async (p) => {
    try {
      await deliveryApi.toggleProdutoDisponibilidade(p.id, !p.disponivel);
      await reload();
    } catch {
      toast.error("Falha ao alterar disponibilidade");
    }
  };

  const handleDeleteProduto = async (p) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remover "${p.nome}"?`)) return;
    try {
      await deliveryApi.deleteProduto(p.id);
      toast.success("Produto removido");
      await reload();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  // ===== Categorias handlers =====
  const handleAddCategoria = async () => {
    const nome = (novaCategoriaNome || "").trim();
    if (!nome) {
      toast.error("Informe um nome");
      return;
    }
    if (!cardapio?.id) {
      toast.error("Cardápio não encontrado");
      return;
    }
    try {
      await deliveryApi.addCategoria(cardapio.id, {
        id: `cat_${Date.now().toString(36)}`,
        nome,
        ordem: categorias.length + 1,
      });
      setNovaCategoriaNome("");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar categoria");
    }
  };

  const handleDeleteCategoria = async (c) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remover categoria "${c.nome}"? Produtos serão desvinculados.`)) return;
    try {
      await deliveryApi.removeCategoria(cardapio.id, c.id);
      await reload();
    } catch {
      toast.error("Falha ao remover categoria");
    }
  };

  // ===== Zonas handlers =====
  const handleSaveZona = async (data) => {
    try {
      const bairros = (data.bairros || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        nome: data.nome,
        descricao: data.descricao,
        bairros,
        taxa_entrega: parseFloat(data.taxa_entrega || 0),
        tempo_estimado_min: parseInt(data.tempo_estimado_min || 30, 10),
        ativo: data.ativo,
      };
      if (editingZona?.id) {
        await deliveryApi.updateZona(editingZona.id, payload);
        toast.success("Zona atualizada");
      } else {
        await deliveryApi.createZona({ loja_id: loja.id, ...payload });
        toast.success("Zona criada");
      }
      setEditingZona(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar zona");
    }
  };

  const handleDeleteZona = async (z) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remover zona "${z.nome}"?`)) return;
    try {
      await deliveryApi.deleteZona(z.id);
      toast.success("Zona removida");
      await reload();
    } catch {
      toast.error("Falha ao remover zona");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (!loja) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <Package className="w-12 h-12 mx-auto text-[var(--text-secondary)] mb-4" />
          <h2 className="text-lg font-semibold mb-2">Delivery não ativado</h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Ative o Delivery primeiro para poder gerenciar produtos e zonas.
          </p>
          <Link
            to={`/empreendedor/delivery/${negocioId}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg"
            data-testid="delivery-go-onboarding"
          >
            Ativar Delivery
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] pb-12">
      <DeliveryNav negocioId={negocioId} negocioNome={loja.nome_loja} />
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <h1 className="text-lg font-bold">Cardápio & Entregas</h1>
            <p className="text-xs text-[var(--text-secondary)]">
              Gerencie produtos, categorias, zonas e horários
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 flex gap-1 border-t border-[var(--border)]">
          <button
            onClick={() => setTab("produtos")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "produtos"
                ? "border-[var(--primary)] text-[var(--primary)]"
                : "border-transparent text-[var(--text-secondary)]"
            }`}
            data-testid="delivery-tab-produtos"
          >
            Produtos ({produtos.length})
          </button>
          <button
            onClick={() => setTab("categorias")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "categorias"
                ? "border-[var(--primary)] text-[var(--primary)]"
                : "border-transparent text-[var(--text-secondary)]"
            }`}
            data-testid="delivery-tab-categorias"
          >
            Categorias ({categorias.length})
          </button>
          <button
            onClick={() => setTab("zonas")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "zonas"
                ? "border-[var(--primary)] text-[var(--primary)]"
                : "border-transparent text-[var(--text-secondary)]"
            }`}
            data-testid="delivery-tab-zonas"
          >
            Zonas de entrega ({zonas.length})
          </button>
          <button
            onClick={() => setTab("loja")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "loja"
                ? "border-[var(--primary)] text-[var(--primary)]"
                : "border-transparent text-[var(--text-secondary)]"
            }`}
            data-testid="delivery-tab-loja"
          >
            Loja & Horários
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {tab === "produtos" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold">Cardápio</h2>
              <button
                onClick={() =>
                  setEditingProduto({ nome: "", descricao: "", preco: "", categoria_id: "", disponivel: true })
                }
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
                data-testid="delivery-new-produto"
              >
                <Plus className="w-4 h-4" /> Novo produto
              </button>
            </div>

            {produtos.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-[var(--border)] p-8 text-center text-sm text-[var(--text-secondary)]">
                Nenhum produto ainda — cadastre seu primeiro item.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {produtos.map((p) => (
                  <div
                    key={p.id}
                    className="bg-white rounded-xl border border-[var(--border)] p-4 flex items-start gap-3"
                    data-testid={`delivery-produto-${p.id}`}
                  >
                    {p.foto_url ? (
                      <img
                        src={resolveBackendImage(p.foto_url)}
                        alt={p.nome}
                        className="w-16 h-16 rounded-lg object-cover bg-[var(--bg-secondary)] shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-[var(--bg-secondary)] flex items-center justify-center shrink-0">
                        <Package className="w-6 h-6 text-[var(--text-secondary)]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{p.nome}</h3>
                        {!p.disponivel && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                            indisponível
                          </span>
                        )}
                        {p.categoria_id && (
                          <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                            {categorias.find((c) => c.id === p.categoria_id)?.nome || "—"}
                          </span>
                        )}
                        {(p.grupos_complementos || []).length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                            {(p.grupos_complementos || []).length} grupo(s) complemento
                          </span>
                        )}
                        {p.estoque?.tipo === "limitado" && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            (p.estoque?.quantidade ?? 0) <= 0
                              ? "bg-gray-200 text-gray-700"
                              : (p.estoque?.quantidade ?? 0) <= (p.estoque?.alerta_minimo ?? 0)
                              ? "bg-amber-100 text-amber-700"
                              : "bg-blue-50 text-blue-700"
                          }`}>
                            estoque: {p.estoque?.quantidade ?? 0}
                          </span>
                        )}
                        {(p.tipo_disponibilidade === "encomenda" || p.tipo_disponibilidade === "ambos") && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                            encomenda
                          </span>
                        )}
                      </div>
                      {p.descricao && (
                        <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">
                          {p.descricao}
                        </p>
                      )}
                      <p className="text-base font-bold text-[var(--primary)] mt-1">
                        {formatBRL(p.preco)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleToggleDisp(p)}
                        className={`p-1.5 rounded ${
                          p.disponivel
                            ? "text-emerald-700 hover:bg-emerald-50"
                            : "text-red-700 hover:bg-red-50"
                        }`}
                        title={p.disponivel ? "Tornar indisponível" : "Tornar disponível"}
                        data-testid={`delivery-toggle-produto-${p.id}`}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          setEditingProduto({
                            id: p.id,
                            nome: p.nome,
                            descricao: p.descricao || "",
                            preco: p.preco,
                            categoria_id: p.categoria_id || "",
                            disponivel: p.disponivel,
                            destaque: !!p.destaque,
                            foto_url: p.foto_url || null,
                            // Fase C
                            unidade_medida: p.unidade_medida || "unidade",
                            tipo_produto: p.tipo_produto || "artesanal",
                            restricoes_alimentares: p.restricoes_alimentares || [],
                            disponibilidade_horario: p.disponibilidade_horario || null,
                            grupos_complementos: p.grupos_complementos || [],
                            tipo_disponibilidade: p.tipo_disponibilidade || "pronta_entrega",
                            encomenda_config: p.encomenda_config || null,
                            estoque: p.estoque || {
                              tipo: "sempre_disponivel",
                              quantidade: 0,
                              alerta_minimo: 0,
                              ao_zerar: "bloquear_automatico",
                            },
                          })
                        }
                        className="p-1.5 hover:bg-[var(--bg-secondary)] rounded"
                        title="Editar"
                        data-testid={`delivery-edit-produto-${p.id}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduto(p)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Remover"
                        data-testid={`delivery-delete-produto-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "categorias" && (
          <div>
            <h2 className="text-base font-semibold mb-3">Categorias do cardápio</h2>
            <p className="text-xs text-[var(--text-secondary)] mb-4">
              Organize seu cardápio em seções (ex.: Pratos, Bebidas, Sobremesas). A
              ordem segue a sequência de criação.
            </p>

            <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4 flex gap-2">
              <input
                type="text"
                value={novaCategoriaNome}
                onChange={(e) => setNovaCategoriaNome(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCategoria()}
                placeholder="Nome da nova categoria"
                className="flex-1 px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="categoria-nome-input"
              />
              <button
                onClick={handleAddCategoria}
                disabled={!novaCategoriaNome.trim()}
                className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="categoria-add"
              >
                <Plus className="w-4 h-4" /> Adicionar
              </button>
            </div>

            {categorias.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-[var(--border)] p-8 text-center text-sm text-[var(--text-secondary)]">
                Sem categorias ainda — todos os produtos aparecem em "Outros".
              </div>
            ) : (
              <div className="space-y-2">
                {categorias.map((c) => {
                  const count = produtos.filter((p) => p.categoria_id === c.id).length;
                  return (
                    <div
                      key={c.id}
                      className="bg-white rounded-xl border border-[var(--border)] p-3 flex items-center gap-3"
                      data-testid={`categoria-${c.id}`}
                    >
                      <span className="w-7 h-7 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center text-xs font-bold">
                        {c.ordem || "—"}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium">{c.nome}</p>
                        <p className="text-xs text-[var(--text-secondary)]">
                          {count} produto(s)
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteCategoria(c)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        data-testid={`categoria-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "zonas" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold">Zonas de entrega</h2>
              <button
                onClick={() =>
                  setEditingZona({
                    nome: "",
                    descricao: "",
                    bairros: "",
                    taxa_entrega: 0,
                    tempo_estimado_min: 30,
                    ativo: true,
                  })
                }
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
                data-testid="delivery-new-zona"
              >
                <Plus className="w-4 h-4" /> Nova zona
              </button>
            </div>

            {zonas.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-[var(--border)] p-8 text-center text-sm text-[var(--text-secondary)]">
                Cadastre ao menos uma zona para começar a aceitar entregas.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {zonas.map((z) => (
                  <div
                    key={z.id}
                    className="bg-white rounded-xl border border-[var(--border)] p-4 flex items-start gap-3"
                    data-testid={`delivery-zona-${z.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[var(--primary)]" />
                        <h3 className="font-semibold truncate">{z.nome}</h3>
                      </div>
                      <p className="text-xs text-[var(--text-secondary)] mt-1">
                        {z.bairros?.join(", ") || "—"}
                      </p>
                      <div className="flex gap-3 mt-2 text-xs">
                        <span>
                          <strong className="text-[var(--primary)]">
                            {formatBRL(z.taxa_entrega)}
                          </strong>{" "}
                          taxa
                        </span>
                        <span>~{z.tempo_estimado_min}min</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() =>
                          setEditingZona({
                            id: z.id,
                            nome: z.nome,
                            descricao: z.descricao || "",
                            bairros: (z.bairros || []).join(", "),
                            taxa_entrega: z.taxa_entrega,
                            tempo_estimado_min: z.tempo_estimado_min,
                            ativo: z.ativo,
                          })
                        }
                        className="p-1.5 hover:bg-[var(--bg-secondary)] rounded"
                        data-testid={`delivery-edit-zona-${z.id}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteZona(z)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        data-testid={`delivery-delete-zona-${z.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "loja" && (
          <LojaSettings loja={loja} onUpdated={reload} />
        )}
      </div>

      {/* Modais */}
      {editingProduto && (
        <ProdutoFormModal
          initial={editingProduto}
          categorias={categorias}
          onSave={handleSaveProduto}
          onClose={() => setEditingProduto(null)}
        />
      )}
      {editingZona && (
        <ZonaFormModal
          initial={editingZona}
          onSave={handleSaveZona}
          onClose={() => setEditingZona(null)}
        />
      )}
    </div>
  );
}

function ProdutoFormModal({ initial, categorias = [], onSave, onClose }) {
  const [data, setData] = useState(() => ({
    nome: "",
    descricao: "",
    preco: 0,
    disponivel: true,
    destaque: false,
    unidade_medida: "unidade",
    tipo_produto: "artesanal",
    restricoes_alimentares: [],
    disponibilidade_horario: null,
    grupos_complementos: [],
    tipo_disponibilidade: "pronta_entrega",
    encomenda_config: null,
    estoque: {
      tipo: "sempre_disponivel",
      quantidade: 0,
      alerta_minimo: 0,
      ao_zerar: "bloquear_automatico",
    },
    ...initial,
  }));
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("info");

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !initial.id) return;
    setUploading(true);
    try {
      const r = await deliveryApi.uploadProdutoFoto(initial.id, file);
      setData((d) => ({ ...d, foto_url: r.foto_url }));
      toast.success(`Foto enviada (${r.compression_ratio}% menor)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar imagem");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handlePhotoDelete = async () => {
    if (!initial.id) return;
    try {
      await deliveryApi.deleteProdutoFoto(initial.id);
      setData((d) => ({ ...d, foto_url: null }));
      toast.success("Foto removida");
    } catch {
      toast.error("Falha ao remover foto");
    }
  };

  // Sync tipo_produto ↔ restricoes (já feito no backend, mas reflete na UI)
  const setTipoProduto = (tp) => {
    let next = [...(data.restricoes_alimentares || [])];
    if ((tp === "organico" || tp === "agroecologico") && !next.includes(tp)) {
      next.push(tp);
    }
    update({ tipo_produto: tp, restricoes_alimentares: next });
  };

  const toggleRestricao = (key) => {
    const cur = data.restricoes_alimentares || [];
    update({
      restricoes_alimentares: cur.includes(key)
        ? cur.filter((x) => x !== key)
        : [...cur, key],
    });
  };

  const TABS = [
    { key: "info", label: "Informações" },
    { key: "comp", label: "Complementos" },
    { key: "disp", label: "Disponibilidade" },
    { key: "estoque", label: "Estoque & Encomenda" },
  ];

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4"
      data-testid="delivery-produto-modal"
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)] bg-white">
          <h3 className="font-semibold">
            {initial.id ? "Editar produto" : "Novo produto"}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[var(--bg-secondary)] rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div
          className="flex border-b border-[var(--border)] overflow-x-auto bg-[var(--bg-secondary)] sticky top-0 z-10"
          data-testid="produto-form-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`produto-form-tab-${t.key}`}
              className={`px-3 py-2.5 text-xs sm:text-sm whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key
                  ? "border-orange-500 text-orange-600 font-semibold bg-white"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-4 overflow-auto flex-1">
          {tab === "info" && (
            <ProdutoFormAbaInfo
              data={data}
              update={update}
              setTipoProduto={setTipoProduto}
              toggleRestricao={toggleRestricao}
              categorias={categorias}
              initial={initial}
              uploading={uploading}
              handlePhotoUpload={handlePhotoUpload}
              handlePhotoDelete={handlePhotoDelete}
            />
          )}
          {tab === "comp" && (
            <ProdutoFormAbaComplementos data={data} update={update} />
          )}
          {tab === "disp" && (
            <ProdutoFormAbaDisponibilidade data={data} update={update} />
          )}
          {tab === "estoque" && (
            <ProdutoFormAbaEstoque data={data} update={update} />
          )}
        </div>

        <div className="p-4 border-t border-[var(--border)] flex gap-2 bg-white">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)]"
            data-testid="delivery-produto-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(data)}
            disabled={!data.nome || data.preco === ""}
            className="flex-1 px-4 py-2 text-sm bg-[var(--primary)] text-white rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="delivery-produto-save"
          >
            <Save className="w-4 h-4" /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function ZonaFormModal({ initial, onSave, onClose }) {
  const [data, setData] = useState(initial);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" data-testid="delivery-zona-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <h3 className="font-semibold">{initial.id ? "Editar zona" : "Nova zona"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-secondary)] rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">Nome *</label>
            <input
              type="text"
              value={data.nome}
              onChange={(e) => setData({ ...data, nome: e.target.value })}
              placeholder="Ex.: Centro, Periferia A"
              className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
              data-testid="delivery-zona-nome"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">
              Bairros atendidos (separados por vírgula)
            </label>
            <input
              type="text"
              value={data.bairros}
              onChange={(e) => setData({ ...data, bairros: e.target.value })}
              placeholder="Centro, Funcionários, Vila Operária"
              className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
              data-testid="delivery-zona-bairros"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Taxa (R$) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={data.taxa_entrega}
                onChange={(e) => setData({ ...data, taxa_entrega: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="delivery-zona-taxa"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Tempo (min)</label>
              <input
                type="number"
                min="1"
                value={data.tempo_estimado_min}
                onChange={(e) => setData({ ...data, tempo_estimado_min: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="delivery-zona-tempo"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!data.ativo}
              onChange={(e) => setData({ ...data, ativo: e.target.checked })}
              data-testid="delivery-zona-ativo"
            />
            Zona ativa
          </label>
        </div>
        <div className="p-4 border-t border-[var(--border)] flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)]"
            data-testid="delivery-zona-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(data)}
            disabled={!data.nome}
            className="flex-1 px-4 py-2 text-sm bg-[var(--primary)] text-white rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="delivery-zona-save"
          >
            <Save className="w-4 h-4" /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

const DIAS = [
  { val: 0, label: "Dom" },
  { val: 1, label: "Seg" },
  { val: 2, label: "Ter" },
  { val: 3, label: "Qua" },
  { val: 4, label: "Qui" },
  { val: 5, label: "Sex" },
  { val: 6, label: "Sáb" },
];

function LojaSettings({ loja, onUpdated }) {
  const [horarios, setHorarios] = useState(loja.horarios_funcionamento || []);
  const [tempoPreparo, setTempoPreparo] = useState(loja.tempo_medio_preparo_min || 30);
  const [aceitaEntrega, setAceitaEntrega] = useState(loja.aceita_entrega ?? true);
  const [aceitaRetirada, setAceitaRetirada] = useState(loja.aceita_retirada ?? true);
  const [saving, setSaving] = useState(false);

  const handleToggleDia = (dia) => {
    setHorarios((prev) => {
      const existing = prev.find((h) => h.dia_semana === dia);
      if (existing) return prev.filter((h) => h.dia_semana !== dia);
      return [...prev, { dia_semana: dia, abertura: "08:00", fechamento: "22:00" }];
    });
  };

  const handleSetHora = (dia, campo, valor) => {
    setHorarios((prev) =>
      prev.map((h) => (h.dia_semana === dia ? { ...h, [campo]: valor } : h))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await deliveryApi.updateLoja(loja.id, {
        horarios_funcionamento: horarios.map((h) => ({
          dia_semana: h.dia_semana,
          abertura: h.abertura,
          fechamento: h.fechamento,
        })),
        tempo_medio_preparo_min: parseInt(tempoPreparo, 10) || 30,
        aceita_entrega: aceitaEntrega,
        aceita_retirada: aceitaRetirada,
      });
      toast.success("Configurações salvas");
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="loja-settings" className="space-y-4">
      <h2 className="text-base font-semibold">Configurações da loja</h2>

      {/* Tempo de preparo + tipos */}
      <div className="bg-white rounded-xl border border-[var(--border)] p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">
            Tempo médio de preparo (min)
          </label>
          <input
            type="number"
            min="1"
            value={tempoPreparo}
            onChange={(e) => setTempoPreparo(e.target.value)}
            className="w-32 px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
            data-testid="loja-tempo-preparo"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={aceitaEntrega}
              onChange={(e) => setAceitaEntrega(e.target.checked)}
              data-testid="loja-aceita-entrega"
            />
            Aceita entrega
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={aceitaRetirada}
              onChange={(e) => setAceitaRetirada(e.target.checked)}
              data-testid="loja-aceita-retirada"
            />
            Aceita retirada
          </label>
        </div>
      </div>

      {/* Horários */}
      <div className="bg-white rounded-xl border border-[var(--border)] p-4">
        <h3 className="text-sm font-semibold mb-1">Horários de funcionamento</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          Selecione os dias que atende e ajuste os horários. Pedidos só são aceitos
          dentro desses intervalos.
        </p>
        <div className="space-y-2">
          {DIAS.map((d) => {
            const ativo = horarios.find((h) => h.dia_semana === d.val);
            return (
              <div
                key={d.val}
                className="flex items-center gap-3"
                data-testid={`loja-dia-${d.val}`}
              >
                <button
                  type="button"
                  onClick={() => handleToggleDia(d.val)}
                  className={`w-12 px-2 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    ativo
                      ? "bg-[var(--primary)] text-white"
                      : "bg-[var(--bg-secondary)] text-[var(--text-secondary)]"
                  }`}
                >
                  {d.label}
                </button>
                {ativo ? (
                  <div className="flex items-center gap-2 text-sm flex-1">
                    <input
                      type="time"
                      value={ativo.abertura}
                      onChange={(e) => handleSetHora(d.val, "abertura", e.target.value)}
                      className="px-2 py-1 border border-[var(--border)] rounded text-sm"
                    />
                    <span className="text-[var(--text-secondary)]">às</span>
                    <input
                      type="time"
                      value={ativo.fechamento}
                      onChange={(e) => handleSetHora(d.val, "fechamento", e.target.value)}
                      className="px-2 py-1 border border-[var(--border)] rounded text-sm"
                    />
                  </div>
                ) : (
                  <span className="text-xs text-[var(--text-secondary)]">Fechado</span>
                )}
              </div>
            );
          })}
        </div>
        {horarios.length === 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
            ⚠️ Sem horários configurados — a loja aceitará pedidos 24/7. Se preferir,
            selecione dias e horários acima.
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="loja-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar configurações
        </button>
      </div>
    </div>
  );
}

